import qs from 'query-string';
import { formatPrice } from '@dashboard/services/formatting';
import getDisplayPrice from '@dashboard/services/getDisplayPrice';
import { createPaletteSearchHook, type PaletteResultRow } from './usePaletteSearch';

function formatPagePrice(page: any): string | undefined {
  const product = page.product ?? page;
  const { amount, currency } = getDisplayPrice(product);
  if (amount == null) return undefined;
  return formatPrice({ amount, currency: currency || 'usd' });
}

function pageSubtitle(page: any): string | undefined {
  const price = formatPagePrice(page);
  const slug = page.slug ? `/${page.slug}` : undefined;
  if (price && slug) return `${price} – ${slug}`;
  return price || slug;
}

const build =
  (basePath: string, extraParams: Record<string, unknown> = {}) =>
  (sellerId: string, query: string) =>
    `/sellers/${sellerId}${basePath}?${qs.stringify({ search: query, limit: 10, ...extraParams })}`;

// 1. Checkout pages — /sellers/:sellerId/pages?search=&limit=10&type=checkout
export const useCheckoutPagesSearch = createPaletteSearchHook({
  groupKey: 'checkout-pages',
  buildPath: build('/pages', { type: 'checkout' }),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.name || row.slug || 'Untitled page',
    subtitle: pageSubtitle(row),
    path: `/pages/${row._id}`,
    pageType: 'checkout',
  }),
});

// 2. Customers — /sellers/:sellerId/customers?search=&limit=5
export const useCustomersSearch = createPaletteSearchHook({
  groupKey: 'customers',
  buildPath: build('/customers'),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.email || row.name || 'Unknown customer',
    subtitle: row.name && row.email ? row.name : undefined,
    path: `/customers/${row._id}`,
  }),
});

// 3. Payments — /sellers/:sellerId/charges?search=&limit=5&pageType=checkout
export const usePaymentsSearch = createPaletteSearchHook({
  groupKey: 'payments',
  buildPath: build('/charges', { pageType: 'checkout' }),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.customerEmail || row.orderId || 'Payment',
    subtitle: row.orderId ? `#${row.orderId}` : undefined,
    path: `/orders/payments/${row._id}`,
  }),
});

// 4. Subscriptions — /sellers/:sellerId/subscriptions?search=&limit=5
export const useSubscriptionsSearch = createPaletteSearchHook({
  groupKey: 'subscriptions',
  buildPath: build('/subscriptions'),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.customerEmail || row.orderId || 'Subscription',
    subtitle: row.orderId ? `#${row.orderId}` : undefined,
    path: `/orders/subscriptions/${row._id}`,
  }),
});

// 5. Bookings — /sellers/:sellerId/charges?search=&limit=5&pageType=event
export const useBookingsSearch = createPaletteSearchHook({
  groupKey: 'bookings',
  buildPath: build('/charges', { pageType: 'event' }),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.customerEmail || row.orderId || 'Booking',
    subtitle: row.orderId ? `#${row.orderId}` : undefined,
    path: `/orders/bookings/${row._id}`,
  }),
});

// 6. Submissions — /sellers/:sellerId/submissions?search=&limit=5
export const useSubmissionsSearch = createPaletteSearchHook({
  groupKey: 'submissions',
  buildPath: build('/submissions'),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.customerEmail || row.billing?.email || 'Submission',
    subtitle: row.customerName || row.billing?.name || undefined,
    path: `/submissions/${row._id}`,
  }),
});

// 7. Events — /sellers/:sellerId/pages?search=&limit=5&type=event
// Feature branch: may return empty on main if no events exist; silently returns [] on error.
export const useEventsSearch = createPaletteSearchHook({
  groupKey: 'events',
  buildPath: build('/pages', { type: 'event' }),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.name || row.slug || 'Untitled event',
    subtitle: row.slug ? `/${row.slug}` : undefined,
    path: `/pages/${row._id}`,
    pageType: 'event',
  }),
});

// 8. Forms — /sellers/:sellerId/pages?search=&limit=5&type=form
// Feature branch: may return empty on main if no forms exist; silently returns [] on error.
export const useFormsSearch = createPaletteSearchHook({
  groupKey: 'forms',
  buildPath: build('/pages', { type: 'form' }),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.name || row.slug || 'Untitled form',
    subtitle: row.slug ? `/${row.slug}` : undefined,
    path: `/pages/${row._id}`,
    pageType: 'form',
  }),
});

// 9. Coupons — /sellers/:sellerId/coupons?search=&limit=10
export const useCouponsSearch = createPaletteSearchHook({
  groupKey: 'coupons',
  buildPath: build('/coupons'),
  mapResult: (row: any): PaletteResultRow => ({
    id: row._id,
    label: row.label || row.code || 'Coupon',
    subtitle: row.label && row.code ? row.code : undefined,
    path: `/coupons/${row._id}`,
  }),
});
